import { computed } from 'vue'

import { useNavItemsStore } from '@/stores/navigationItems'

export const fetchNavigationMenu = async () => {
  const navItemsStore = useNavItemsStore()

  try {
    await navItemsStore.fetchButterNav()
    await navItemsStore.fetchButterSecondNav()
    await navItemsStore.fetchButterThirdNav()
    await navItemsStore.fetchButterFourthNav()
    await navItemsStore.fetchButterSecondQuickLinks()
  } catch (error) {
    console.error('Error during fetching main nav:', error)
  } finally {
    navItemsStore.updateIsLoaded(true)
  }
}

const mappedNavItems = (main: any, second: any, quickLinks: any, level4: any) => {
  const secondMap = new Map<number, any>()
  second.forEach((item: any) => {
    if (item.meta && item.meta.id) {
      const id = parseInt(item.meta.id)
      if (!isNaN(id)) {
        secondMap.set(id, item)
      }
    }
  })

  const quickLinksMap = new Map<number, any>()
  quickLinks.forEach((item: any) => {
    if (item.meta && item.meta.id) {
      const id = item.meta.id
      if (!isNaN(id)) {
        quickLinksMap.set(id, item)
      }
    }
  })

  const level4Map = new Map<number, any>()
  level4.forEach((item: any) => {
    if (item.meta && item.meta.id) {
      const id = parseInt(item.meta.id)
      if (!isNaN(id)) {
        level4Map.set(id, item)
      }
    }
  })

  // Helper function to extract IDs and map items
  const extractAndMapItems = (items: any[], map: Map<number, any>) => {
    return items.map((item: any) => {
      if (item && typeof item === 'string') {
        const match = item?.match(/_id=(\d+)/)
        if (match) {
          const id = parseInt(match[1])
          return map.get(id) || {}
        }
      }
      return {}
    })
  }

  // Map main items with their respective child items, quick links, and level 4 items
  const mappedItems = main.map((mainItem: any) => {
    const childItems = mainItem.child_items.map((childItem: any) => {
      if (childItem && typeof childItem === 'string') {
        const match = childItem?.match(/_id=(\d+)/)
        if (match) {
          const id = parseInt(match[1])
          const secondItem = secondMap.get(id) || {}

          // Map level 3 items to their level 4 items
          if (secondItem.menu_level_three_items) {
            secondItem.menu_level_three_items = secondItem.menu_level_three_items.map(
              (level3Item: any) => {
                if (level3Item.levelfouritems) {
                  level3Item.levelfouritems = extractAndMapItems(
                    level3Item.levelfouritems,
                    level4Map
                  )
                }
                return level3Item
              }
            )
          }

          return secondItem
        }
      }
      return {}
    })

    const quickLinkItems = (mainItem.quicklinksitems || []).map((quickLinkItem: any) => {
      if (quickLinkItem && typeof quickLinkItem === 'string') {
        const match = quickLinkItem?.match(/_id=(\d+)/)
        if (match) {
          const id = parseInt(match[1])
          return quickLinksMap.get(id) || {}
        }
      }
      return {}
    })

    return {
      ...mainItem,
      child_items: childItems,
      quicklinksitems: quickLinkItems
    }
  })

  const navItemsStore = useNavItemsStore()
  navItemsStore.updateMapNavItems(mappedItems)

  return mappedItems
}
export const generateBreadcrumbsForUrl = async function (
  targetUrl: string,
  level2ItemUrl?: string,
  level3ItemUrl?: string,
  level4ItemUrl?: string
) {
  const navItemsStore = useNavItemsStore()

  await fetchNavigationMenu()
  const breadcrumbs: any = []
  const mappedItems = navItemsStore.mappedNavItems
  // Helper function to recursively generate breadcrumbs
  const buildBreadcrumbs = (item: any, level: number) => {
    if (item && (item.label || item.menu_item_label) && item.url) {
      breadcrumbs.push({
        level: `level${level}`,
        title: item.label ?? item.menu_item_label,
        description: item?.description ?? '',
        url: item.url
      })

      // Check for child_items to go deeper into the hierarchy
      if (item.child_items && item.child_items.length > 0) {
        item.child_items.forEach((childItem: any) => {
          if (!level2ItemUrl || childItem.url === level2ItemUrl) {
            buildBreadcrumbs(childItem, level + 1)
          }
        })
      }

      // Check for menu_level_three_items
      if (item.menu_level_three_items && item.menu_level_three_items.length > 0) {
        item.menu_level_three_items.forEach((level3Item: any) => {
          if (!level3ItemUrl || level3Item.url === level3ItemUrl) {
            buildBreadcrumbs(level3Item, level + 1)
          }
        })
      }

      // Check for levelfouritems
      if (item.levelfouritems && item.levelfouritems.length > 0) {
        item.levelfouritems.forEach((level4Item: any) => {
          if (!level4ItemUrl || level4Item.url === level4ItemUrl) {
            buildBreadcrumbs(level4Item, level + 1)
          }
        })
      }
    }
  }

  // Start by finding the main item that matches the target URL
  const mainItem = mappedItems.find((item: any) => item.url === targetUrl)

  if (mainItem) {
    buildBreadcrumbs(mainItem, 1)
  }

  return breadcrumbs
}

export const generatePrevNextForUrl = async function (targetUrl: string) {
  const navItemsStore = useNavItemsStore()

  await fetchNavigationMenu()
  const breadcrumbs: any = []
  const combinedUrls: any = []
  const mappedItems = navItemsStore.mappedNavItems
  // Helper function to recursively generate breadcrumbs
  const buildBreadcrumbs = (item: any, level: number) => {
    if (item && (item.label || item.menu_item_label) && item.url) {
      breadcrumbs.push({
        level: `level${level}`,
        title: item.label ?? item.menu_item_label,
        url: item.url
      })

      // Check for child_items to go deeper into the hierarchy
      if (item.child_items && item.child_items.length > 0) {
        item.child_items.forEach((childItem: any) => {
          buildBreadcrumbs(childItem, level + 1)
        })
      }

      // Check for menu_level_three_items
      if (item.menu_level_three_items && item.menu_level_three_items.length > 0) {
        item.menu_level_three_items.forEach((level3Item: any) => {
          buildBreadcrumbs(level3Item, level + 1)
        })
      }

      // Check for levelfouritems
      if (item.levelfouritems && item.levelfouritems.length > 0) {
        item.levelfouritems.forEach((level4Item: any) => {
          buildBreadcrumbs(level4Item, level + 1)
        })
      }
    }
  }

  // Start by finding the main item that matches the target URL
  const mainItem = mappedItems.find((item: any) => item.url === targetUrl)

  if (mainItem) {
    buildBreadcrumbs(mainItem, 1)
  }

  let currentLevel2: any = null

  breadcrumbs.forEach((item: any) => {
    if (item.level === 'level2') {
      currentLevel2 = item // Set the current level2
    }

    if (item.level === 'level4' && currentLevel2) {
      combinedUrls.push({
        combinedUrl: `${currentLevel2.url}/${item.url}`,
        title: ` ${currentLevel2.title} / ${item.title}`
      })
    }

    if (
      item.level === 'level2' &&
      !breadcrumbs.some((bc: any) => bc.level === 'level4' && currentLevel2)
    ) {
      currentLevel2 = null // Reset if there are no level4s under this level2
    }
  })

  return { combinedUrls }
}
export const useNavigationData = async () => {
  await fetchNavigationMenu()
  const navItemsStore = useNavItemsStore()

  return mappedNavItems(
    navItemsStore.getNavItems,
    navItemsStore.getNavSecondItems,
    navItemsStore.getNavQuickLinks,
    navItemsStore.getNavFourthItems
  )
}

export const extractInstitutionalChildItems = async () => {
  const navItemsStore = useNavItemsStore()

  const institutionalItem = navItemsStore.getNavUpper.upper_nav.find(
    (item: any) => item.label === 'Institutional'
  )

  return institutionalItem ? institutionalItem.child_items : []
}

export const getLoadedState = () => {
  const navItemsStore = useNavItemsStore()

  return computed(() => navItemsStore.getIsLoaded)
}

export const mapped = () => {
  const navItemsStore = useNavItemsStore()

  return computed(() => navItemsStore.getmappedNavItems)
}
export const isSponsorshipAtleticoPresent = () => {
  // const navItemsStore = useNavItemsStore()
  return computed(() => {
    // return navItemsStore.getNavSecondItems.some(
    //   (item: any) => item.url === 'sponsorships/atletico-de-madrid'
    // )
    return true
  })
}

export const isSponsorshipFortalezaPresent = () => {
  const navItemsStore = useNavItemsStore()
  return computed(() => {
    return navItemsStore.getNavThirdItems.some((item: any) => item.url === 'fortaleza')
  })
}
